import { FormEvent, useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { ChevronLeft } from "lucide-react"
import { createStoryEntry } from "~/api/story"
import { PhotoUploadPanel } from "~/components/PhotoUploadPanel"
import { PageShell } from "~/components/PageShell"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { useProfile } from "~/hooks/useProfile"

const MEMORY_TYPES = [
  { value: "observation", label: "Observation" },
  { value: "photo", label: "Photo" },
  { value: "note", label: "Note" },
] as const

type MemoryType = (typeof MEMORY_TYPES)[number]["value"]

export function StoryNewPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile } = useProfile()
  const initialType = (searchParams.get("type") as MemoryType | null) ?? "observation"
  const [entryType, setEntryType] = useState<MemoryType>(
    MEMORY_TYPES.some((type) => type.value === initialType) ? initialType : "observation",
  )
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [photo, setPhoto] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dogIdFromQuery = searchParams.get("dog_id")
  const dogId =
    (dogIdFromQuery && Number.isFinite(Number(dogIdFromQuery))
      ? Number(dogIdFromQuery)
      : undefined) ?? profile?.active_dog_profile_id
  const dogName =
    profile?.dogs?.find((dog) => dog.id === dogId)?.name ??
    profile?.dog_profile?.name ??
    "your dog"
  const storyBackPath = dogId ? `/story?dog_id=${dogId}` : "/story"
  const heading = useMemo(() => {
    if (entryType === "observation") return `Add an observation about ${dogName}`
    if (entryType === "photo") return `Add a photo memory for ${dogName}`
    return `Add a note to ${dogName}’s Story`
  }, [dogName, entryType])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!title.trim() || saving) return
    if (entryType === "photo" && !photo) {
      setError("Choose a photo before saving")
      return
    }
    setSaving(true)
    setError(null)
    try {
      const result = await createStoryEntry(
        dogId ?? undefined,
        {
          entry_type: entryType,
          title: title.trim(),
          body: body.trim() || undefined,
          occurred_at: new Date().toISOString(),
        },
        entryType === "photo" ? photo : null,
      )
      const detailQuery = dogId ? `?dog_id=${dogId}` : ""
      navigate(`/story/${result.entry.id}${detailQuery}`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save memory")
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <header className="mb-4 flex items-center gap-3">
          <Link
            to={storyBackPath}
            aria-label="Back to Story"
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={20} aria-hidden />
          </Link>
          <h1 className="flex-1 text-lg font-semibold text-[var(--color-text-primary)]">
            Add Memory
          </h1>
        </header>

        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">{heading}</p>
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">
              Memory type
            </legend>
            <div className="flex flex-wrap gap-2">
              {MEMORY_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setEntryType(type.value)}
                  aria-pressed={entryType === type.value}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium ${
                    entryType === type.value
                      ? "border-[var(--color-brand-primary)] bg-[var(--color-brand-primary)] text-white"
                      : "border-[var(--color-divider)] text-[var(--color-text-secondary)]"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </fieldset>

          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Title
            <input
              type="text"
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-sm"
            />
          </label>

          {entryType === "photo" ? (
            <PhotoUploadPanel
              dogName={dogName}
              photo={photo}
              previewUrl={previewUrl}
              error={null}
              purpose="story"
              required
              onPhotoSelected={(file, preview) => {
                setPhoto(file)
                setPreviewUrl(preview)
                setError(null)
              }}
              onClear={() => {
                setPhoto(null)
                setPreviewUrl(null)
              }}
            />
          ) : null}

          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Details
            <textarea
              rows={5}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder={
                entryType === "photo"
                  ? "Describe the moment this photo captures…"
                  : "What happened, and why does it matter?"
              }
              className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-sm"
            />
          </label>

          {error && (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={saving || !title.trim() || (entryType === "photo" && !photo)}
            className="w-full rounded-lg bg-[var(--color-brand-primary)] px-4 py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save memory"}
          </button>
        </form>
      </LoggedInScreenCard>
    </PageShell>
  )
}
